import React from 'react';
import { useRouter } from 'next/router';
import { Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Product } from '@pizza-hut-us-development/client-core';
import { useDecision } from '@optimizely/react-sdk';
import CategoryTitle from '@/common/components/CategoryTitle';
import { TAVERN_RECIPES_TITLE, SIGNATURE_RECIPES_TITLE } from '@/menu/pizza/constants';
import ProductId from '@/common/ProductId';
import Routes from '@/router/routes';
import MenuTile from '@/shared/molecules/MenuTile';
import { useGetPizzaMenu } from '../../hooks/useGetPizzaMenu';
import useOneClickAnalytics from '@/homepage/HutFavoritesSection/hooks/useOneClickAnalytics';
import {
  SodiumWarningDisclaimer
} from '@/clientCore/components/SodiumWarningDisclaimer/components/SodiumWarningDisclaimer';
import useSodiumAlertForStore from '@/clientCore/components/SodiumWarningDisclaimer/hooks/useSodiumAlertForStore';
import LocalizedMenuSkeleton from '@/menu/deals/LocalizedMenu/LocalizedMenuSkeleton';
import { usePizzaMenuPageViewAnalytics } from '../../hooks/usePizzaMenuAnalytics';

export const PIZZA_MENU_TAVERN_TESTID = 'pizza-menu-tavern-container';
export const PIZZA_MENU_SIGNATURE_TESTID = 'pizza-menu-signature-container';

interface RenderPizzaSectionProps {
    pizzas: Product[];
    title: string;
    testId: string;
    startingIndex: number;
  }

const useStyles = makeStyles({
  grid: {
    gap: '2rem',
    marginBottom: '4rem'
  }
});

export const PizzaMenu = (): JSX.Element => {
  const router = useRouter();
  const classes = useStyles();
  const [{enabled: isSignaturePizzaMovedUp}] = useDecision('dtg-1636-move-signature-pizzas-up');
  const {
    cyoPizza,
    tavernPizzas,
    signaturePizzas,
    isLoading
  } = useGetPizzaMenu();
  const tavernPizzaSection = {
    pizzas: tavernPizzas,
    title: TAVERN_RECIPES_TITLE,
    testId: PIZZA_MENU_TAVERN_TESTID,
    startingIndex: cyoPizza ? 1 : 0
  };
  const signaturePizzaSection = {
    pizzas: signaturePizzas,
    title: SIGNATURE_RECIPES_TITLE,
    testId: PIZZA_MENU_SIGNATURE_TESTID,
    startingIndex: (cyoPizza ? 1 : 0) + tavernPizzas.length
  };
  const pizzaSections: RenderPizzaSectionProps[] = isSignaturePizzaMovedUp ?
    [signaturePizzaSection, tavernPizzaSection] :
    [tavernPizzaSection, signaturePizzaSection];
  const {
    onPizzaMenuCustomizeLinkClick, onPizzaMenuAddToCartClick, onPizzaMenuAddToCartSuccess, onGetStartedLinkClick
  } = useOneClickAnalytics();
  usePizzaMenuPageViewAnalytics(cyoPizza, tavernPizzas, signaturePizzas, isLoading);
  const { isShowSodiumAlertForStore } = useSodiumAlertForStore();

  function onPizzaClick(pizza: Product) {
    if (pizza.outOfStock) return;
    const { globalId } = new ProductId(pizza.id);
    router.push(`${Routes.MENU.PIZZA_BUILDER}?id=${globalId}`);
  }

  const renderPizzaSection = ({pizzas, title, testId, startingIndex}: RenderPizzaSectionProps, key?: string | number) => (
    <Grid container className={classes.grid} data-testid={testId} key={key}>
      <Typography variant="h2">{title}</Typography>
      {pizzas.map((tile, index) => (
        <MenuTile
          key={tile.id}
          product={tile}
          handleClick={() => onPizzaClick(tile)}
          handleAnalyticsCustomizeLinkClick={() => onPizzaMenuCustomizeLinkClick(tile, index + startingIndex)}
          handleAnalyticsAddToCartClick={(quantity) => onPizzaMenuAddToCartClick(tile, index + startingIndex, quantity)}
          handleAnalyticsAddToCartSuccess={(quantity) => onPizzaMenuAddToCartSuccess(tile, index + startingIndex, quantity)}
          handleAnalyticsGetStartedClick={() => onGetStartedLinkClick(tile, index + startingIndex)}
        />
      ))}
    </Grid>
  );

  if (isLoading) {
    return <LocalizedMenuSkeleton />;
  }

  return (
    <Grid>
      <CategoryTitle title="Pizza" />
      {cyoPizza && (
        <Grid container className={classes.grid}>
          <MenuTile
            product={cyoPizza}
            handleClick={() => onPizzaClick(cyoPizza)}
            isCyo
            handleAnalyticsGetStartedClick={() => onGetStartedLinkClick(cyoPizza, 0)}
          />
        </Grid>
      )}
      {pizzaSections.map(({ pizzas, title, testId, startingIndex }, idx) =>
        !!pizzas.length && renderPizzaSection({
          pizzas,
          title,
          testId,
          startingIndex
        }, testId || title || idx)
      )}
      {isShowSodiumAlertForStore && <SodiumWarningDisclaimer />}
    </Grid>
  );
};

export default PizzaMenu;
